/* Iter53 P25 — Role-Aware Dashboard Widget.
 *
 * Renders a per-role dashboard using GET /api/dashboard/role-stats:
 *  - Period selector (Luna curentă / Luna trecută / Trimestru curent / An curent / Custom luni)
 *  - Target picker (admin/BO only — choose agency or agent)
 *  - KPI cards: Total, Comisionabil, KO, Probleme, Activate, CSD
 *  - Obiectiv card: ob. lunar setat, daily static, daily dynamic recalculat, achieved%, on_track
 *  - Progress chart (cumulative production vs target_line — LineChart)
 *  - Product Kind chart (BarChart)
 *  - Uploaded vs CSD chart (BarChart)
 *
 * Visible for all roles (agent/agentie/manager/admin/backoffice).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Cell,
} from "recharts";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { Skeleton } from "./ui/skeleton";
import {
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  DollarSign,
  Package,
  Calendar,
  RefreshCw,
  Settings,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// ---------- Period helpers ----------

const todayISO = () => new Date().toISOString().slice(0, 10);

const PERIOD_PRESETS = [
  { key: "this_month", label: "Luna curentă", period: "month", offset: 0 },
  { key: "last_month", label: "Luna trecută", period: "month", offset: -1 },
  { key: "this_quarter", label: "Trimestrul curent", period: "quarter", offset: 0 },
  { key: "this_year", label: "Anul curent", period: "year", offset: 0 },
  { key: "last_year", label: "Anul trecut", period: "year", offset: -1 },
];

function presetToParams(preset) {
  const now = new Date();
  if (preset.period === "month") {
    const m = now.getMonth() + 1 + preset.offset; // 1..12 + offset
    const y = now.getFullYear() + Math.floor((m - 1) / 12);
    const mm = ((m - 1) % 12 + 12) % 12 + 1;
    return { period: "month", period_start: `${y}-${String(mm).padStart(2, "0")}-01` };
  }
  if (preset.period === "quarter") {
    // current quarter start (offset 0 for now)
    const q = Math.floor(now.getMonth() / 3);
    return { period: "quarter", period_start: `${now.getFullYear()}-${String(3 * q + 1).padStart(2, "0")}-01` };
  }
  const y = now.getFullYear() + (preset.offset || 0);
  return { period: "year", period_start: `${y}-01-01` };
}

// Iter62g — intervalul de luni acoperit de preset (pentru obiectivele pe canal × produs)
function presetToMonthRange(preset) {
  const now = new Date();
  if (preset.period === "month") {
    const m0 = now.getMonth() + 1 + preset.offset;
    const y = now.getFullYear() + Math.floor((m0 - 1) / 12);
    const m = ((m0 - 1) % 12 + 12) % 12 + 1;
    return { year: y, mstart: m, mend: m };
  }
  if (preset.period === "quarter") {
    const q = Math.floor(now.getMonth() / 3);
    return { year: now.getFullYear(), mstart: 3 * q + 1, mend: 3 * q + 3 };
  }
  const y = now.getFullYear() + (preset.offset || 0);
  return { year: y, mstart: 1, mend: 12 };
}

// ---------- Product kind chart colors ----------
const KIND_COLORS = {
  EE: "#f59e0b",
  Gaz: "#fb923c",
  VAS: "#6366f1",
  HVAS: "#a855f7",
  "Combo EE + VAS": "#0ea5e9",
  "Combo Gaz + VAS": "#14b8a6",
  unset: "#94a3b8",
};

// ---------- Component ----------

export default function RoleDashboardWidget({ user, agents = [], agencies = [], viewAsAgentId = "" }) {
  const isAdminView = ["admin", "backoffice"].includes(user?.role);
  const isAgentRole = user?.role === "agent";
  const isAgencyRole = user?.role === "agentie";

  // Period state
  const [presetKey, setPresetKey] = useState("this_month");
  const preset = PERIOD_PRESETS.find((p) => p.key === presetKey) || PERIOD_PRESETS[0];

  // Target picker (admin/BO + agencies can choose)
  // For admin/BO: can target admin (whole company), agency or specific agent
  // For agentie: defaults to own agency, can switch to agent within agency
  const [targetMode, setTargetMode] = useState(isAdminView ? "admin" : isAgencyRole ? "agency" : "agent");
  const [targetId, setTargetId] = useState(""); // empty → use role-default

  // Iter53 P26 — when parent's "Vezi ca" dropdown changes, sync widget target to that agent.
  // This unifies the two selectors so the new widget filters consistently with legacy KPIs.
  useEffect(() => {
    if (!isAdminView) return; // only admin/BO use the View-as dropdown
    if (viewAsAgentId) {
      setTargetMode("agent");
      setTargetId(viewAsAgentId);
    } else {
      // "Vezi ca" cleared → revert widget to company-wide view
      setTargetMode("admin");
      setTargetId("");
    }
  }, [viewAsAgentId, isAdminView]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateBasis, setDateBasis] = useState("signed"); // signed = data vânzării | csd = data CSD
  const basisLabel = dateBasis === "csd" ? "după data CSD" : "după data vânzării";

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { period, period_start } = presetToParams(preset);
      const params = new URLSearchParams({ period, period_start });
      params.set("date_basis", dateBasis);
      if (targetMode !== "admin" || isAgencyRole) {
        // Always pass target for non-admin or when explicit
        if ((targetMode === "agency" || targetMode === "agent") && targetId) {
          params.set("target_type", targetMode);
          params.set("target_id", targetId);
        } else if (targetMode === "admin" && isAdminView) {
          params.set("target_type", "admin");
        } else if (isAgencyRole && targetMode === "agency") {
          params.set("target_type", "agency");
          params.set("target_id", user?.agency_id || "");
        }
      } else {
        params.set("target_type", "admin");
      }
      const r = await axios.get(`${API_URL}/dashboard/role-stats?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error("Eroare la încărcarea statisticilor pe rol");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [presetKey, targetMode, targetId, user?.agency_id, preset, isAdminView, isAgencyRole, dateBasis]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Iter62g — obiectivele companiei (suma canalelor din secțiunea Obiective) pentru
  // vizualizarea globală: atingere după data CSD + „încărcat" după data vânzării.
  const isCompanyView = isAdminView && targetMode === "admin";
  const [companyObj, setCompanyObj] = useState(null);
  const fetchCompanyObjectives = useCallback(async () => {
    if (!isCompanyView) { setCompanyObj(null); return; }
    try {
      const { year, mstart, mend } = presetToMonthRange(preset);
      const r = await axios.get(`${API_URL}/channel-objectives/company-progress`, {
        params: { year, mstart, mend },
      });
      setCompanyObj(r.data);
    } catch (e) {
      setCompanyObj(null);
    }
  }, [isCompanyView, preset]);
  useEffect(() => { fetchCompanyObjectives(); }, [fetchCompanyObjectives]);

  // Derived chart data: shape progress_chart for Recharts
  const chartData = useMemo(() => {
    if (!data?.progress_chart) return [];
    return data.progress_chart.map((row) => ({
      day: row.date?.slice(8, 10), // DD
      date: row.date,
      cumulative: row.cumulative,
      target_line: row.target_line,
    }));
  }, [data]);

  const pkData = useMemo(() => {
    if (!data?.by_product_kind) return [];
    return Object.entries(data.by_product_kind).map(([k, v]) => ({
      kind: k === "unset" ? "Neclasificat" : k,
      count: v,
      color: KIND_COLORS[k] || "#94a3b8",
    }));
  }, [data]);

  const csdData = useMemo(() => {
    if (!data?.csd_vs_uploaded) return [];
    return [
      { name: "Încărcat", value: data.csd_vs_uploaded.uploaded, color: "#1e3a8a" },
      { name: "CSD", value: data.csd_vs_uploaded.csd, color: "#10b981" },
    ];
  }, [data]);

  // Period available agencies / agents based on permissions
  const agencyOptions = useMemo(() => {
    if (isAdminView) return agencies;
    if (isAgencyRole) return agencies.filter((a) => a.id === user?.agency_id);
    return [];
  }, [agencies, isAdminView, isAgencyRole, user?.agency_id]);

  const agentOptions = useMemo(() => {
    if (isAdminView) return agents;
    if (isAgencyRole) {
      const myId = user?.agency_id;
      return agents.filter((a) => a.agency_id === myId);
    }
    return agents.filter((a) => a.linked_user_id === user?.id);
  }, [agents, isAdminView, isAgencyRole, user?.agency_id, user?.id]);

  return (
    <Card className="border-l-4 border-l-[#1e3a8a] shadow-sm" data-testid="role-dashboard-widget">
      <CardHeader className="pb-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-[#1e3a8a]" />
              Producție &amp; Obiective
            </CardTitle>
            <CardDescription>
              {data?.target?.name
                ? <>Vizualizare: <span className="font-medium text-slate-700">{data.target.name}</span> · {data?.period?.label || "—"}</>
                : <>Vizualizare globală · {data?.period?.label || "—"}</>}
              {" · "}<span className="font-medium text-emerald-700">{basisLabel}</span>
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Period preset chips */}
            <div className="flex flex-wrap gap-1" data-testid="period-preset-chips">
              {PERIOD_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPresetKey(p.key)}
                  className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                    presetKey === p.key
                      ? "bg-[#1e3a8a] text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`period-chip-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {/* Bază de timp: data vânzării vs data CSD */}
            <div className="inline-flex rounded-full border border-slate-200 p-0.5" data-testid="date-basis-toggle" title="Baza de timp pentru toți indicatorii">
              <button
                type="button"
                onClick={() => setDateBasis("signed")}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${dateBasis === "signed" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid="date-basis-signed"
              >
                Data vânzării
              </button>
              <button
                type="button"
                onClick={() => setDateBasis("csd")}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${dateBasis === "csd" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid="date-basis-csd"
              >
                Data CSD
              </button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              className="h-7 px-2"
              data-testid="role-dashboard-refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Target selector (admin/BO + agentie) */}
        {(isAdminView || isAgencyRole) && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid="target-selector">
            <span className="text-slate-500 uppercase tracking-wide">Vizualizare:</span>
            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
              {isAdminView && (
                <button
                  type="button"
                  onClick={() => { setTargetMode("admin"); setTargetId(""); }}
                  className={`rounded px-2 py-1 text-xs font-medium ${
                    targetMode === "admin" ? "bg-[#1e3a8a] text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                  data-testid="target-mode-admin"
                >
                  Companie
                </button>
              )}
              <button
                type="button"
                onClick={() => { setTargetMode("agency"); setTargetId(isAgencyRole ? user?.agency_id : ""); }}
                className={`rounded px-2 py-1 text-xs font-medium ${
                  targetMode === "agency" ? "bg-[#1e3a8a] text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid="target-mode-agency"
              >
                Agenție
              </button>
              <button
                type="button"
                onClick={() => { setTargetMode("agent"); setTargetId(""); }}
                className={`rounded px-2 py-1 text-xs font-medium ${
                  targetMode === "agent" ? "bg-[#1e3a8a] text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid="target-mode-agent"
              >
                Agent
              </button>
            </div>
            {targetMode === "agency" && (
              <Select value={targetId} onValueChange={setTargetId} data-testid="agency-target-picker">
                <SelectTrigger className="h-8 w-56 text-xs">
                  <SelectValue placeholder="Selectează agenția…" />
                </SelectTrigger>
                <SelectContent>
                  {agencyOptions.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {targetMode === "agent" && (
              <Select value={targetId} onValueChange={setTargetId} data-testid="agent-target-picker">
                <SelectTrigger className="h-8 w-56 text-xs">
                  <SelectValue placeholder="Selectează agentul…" />
                </SelectTrigger>
                <SelectContent>
                  {agentOptions.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.full_name || a.email || a.id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {(targetMode === "agency" || targetMode === "agent") && targetId && (
              <Link
                to={`/objectives?target_type=${targetMode}&target_id=${targetId}`}
                className="ml-auto inline-flex items-center gap-1 text-xs text-[#1e3a8a] hover:underline"
                data-testid="set-objective-link"
              >
                <Settings className="h-3 w-3" />
                Setează obiectiv
              </Link>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-5">
        {loading && (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        )}

        {!loading && data && (
          <>
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 md:gap-3" data-testid="role-kpi-row">
              <KpiTile
                testId="role-kpi-total"
                label="Încărcat"
                value={data.production.total}
                icon={<Package className="h-4 w-4" />}
                color="text-slate-800"
                bg="bg-slate-50"
              />
              <KpiTile
                testId="role-kpi-activated"
                label="Activate"
                value={data.production.activated}
                icon={<CheckCircle2 className="h-4 w-4" />}
                color="text-emerald-700"
                bg="bg-emerald-50"
              />
              <KpiTile
                testId="role-kpi-commissionable"
                label="Comisionabile"
                value={data.production.commissionable}
                icon={<DollarSign className="h-4 w-4" />}
                color="text-amber-700"
                bg="bg-amber-50"
              />
              <KpiTile
                testId="role-kpi-ko"
                label="KO (Anulate+Reziliate)"
                value={data.production.ko}
                icon={<XCircle className="h-4 w-4" />}
                color="text-rose-700"
                bg="bg-rose-50"
              />
              <KpiTile
                testId="role-kpi-problems"
                label="Probleme (Rework)"
                value={data.production.problems}
                icon={<AlertTriangle className="h-4 w-4" />}
                color="text-orange-700"
                bg="bg-orange-50"
              />
              <KpiTile
                testId="role-kpi-csd"
                label="CSD"
                value={data.csd_vs_uploaded.csd}
                icon={<Calendar className="h-4 w-4" />}
                color="text-sky-700"
                bg="bg-sky-50"
              />
            </div>

            {/* Objective Card — Iter62g: vizualizarea globală folosește obiectivele
                pe canal × produs (companie); agenție/agent păstrează obiectivul propriu */}
            {isCompanyView && companyObj ? (
              <CompanyObjectivesBlock cp={companyObj} periodLabel={data.period?.label} />
            ) : (
              <ObjectiveBlock data={data} />
            )}

            {/* Progress Chart */}
            {chartData.length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="progress-chart-box">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                    <TrendingUp className="h-4 w-4 text-[#1e3a8a]" />
                    Producție vs Obiectiv {data.period?.label ? `· ${data.period.label}` : ""}
                  </h4>
                  {data.objective?.value > 0 && (
                    <Badge
                      className={`text-[10px] ${
                        data.objective.on_track ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                      }`}
                      data-testid="on-track-badge"
                    >
                      {data.objective.on_track ? "On track" : "În urmă"}
                    </Badge>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ fontSize: 11, borderRadius: 6 }}
                      labelFormatter={(v) => `Ziua ${v}`}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {data.objective?.value > 0 && (
                      <ReferenceLine y={data.objective.value} stroke="#0ea5e9" strokeDasharray="5 5" label={{ value: `Țintă ${data.objective.value}`, position: "right", fontSize: 10, fill: "#0ea5e9" }} />
                    )}
                    <Line type="monotone" dataKey="cumulative" name="Producție cumulativă" stroke="#1e3a8a" strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
                    {data.objective?.value > 0 && (
                      <Line type="monotone" dataKey="target_line" name="Linie țintă (liniar)" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Bottom row: Product kind + CSD vs Uploaded */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Product Kind */}
              <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="product-kind-chart-box">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Distribuție Tip Produs</h4>
                {pkData.length === 0 ? (
                  <p className="text-xs text-slate-400 py-6 text-center">Niciun produs în perioada selectată.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={pkData} layout="vertical" margin={{ top: 5, right: 10, left: 60, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="kind" tick={{ fontSize: 11 }} width={100} />
                      <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                      <Bar dataKey="count" name="Contracte">
                        {pkData.map((p, i) => (
                          <Cell key={i} fill={p.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Uploaded vs CSD */}
              <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="uploaded-vs-csd-chart-box">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Încărcat vs CSD</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={csdData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} />
                    <Bar dataKey="value" name="Contracte" fill="#1e3a8a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                {data.csd_vs_uploaded.uploaded > 0 && (
                  <p className="text-[11px] text-slate-500 mt-2 text-center">
                    Rata CSD: <span className="font-medium">
                      {Math.round((data.csd_vs_uploaded.csd / data.csd_vs_uploaded.uploaded) * 1000) / 10}%
                    </span>
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Sub-components ----------

function KpiTile({ label, value, icon, color, bg, testId }) {
  return (
    <div className={`rounded-lg ${bg} p-2.5 md:p-3 border border-transparent`} data-testid={testId}>
      <div className="flex items-center justify-between gap-1">
        <span className={`text-[10px] md:text-xs uppercase tracking-wide font-medium ${color}`}>{label}</span>
        <span className={`${color} opacity-70`}>{icon}</span>
      </div>
      <p className={`text-xl md:text-2xl font-bold mt-1 ${color}`}>
        {(value ?? 0).toLocaleString("ro-RO")}
      </p>
    </div>
  );
}

function ObjectiveBlock({ data }) {
  const obj = data.objective || {};
  const daily = data.daily_objective || {};
  const period = data.period || {};
  const hasObjective = obj.found && obj.value > 0;

  if (!hasObjective) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50/60 p-4 text-center" data-testid="no-objective-block">
        <Target className="h-6 w-6 text-slate-400 mx-auto mb-1" />
        <p className="text-sm text-slate-600">
          Nu există obiectiv setat pentru această perioadă.
        </p>
        {data.target?.id && (
          <Link
            to={`/objectives?target_type=${data.target.type}&target_id=${data.target.id}`}
            className="mt-2 inline-flex items-center gap-1 text-xs text-[#1e3a8a] hover:underline"
            data-testid="set-objective-cta"
          >
            <Settings className="h-3 w-3" />
            Setează obiectiv lunar
          </Link>
        )}
      </div>
    );
  }

  const progressPct = Math.min(obj.achieved_pct || 0, 100);
  return (
    <div className="rounded-lg border border-[#1e3a8a]/20 bg-gradient-to-r from-[#1e3a8a]/5 to-sky-50 p-3 md:p-4" data-testid="objective-block">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Obiectiv lunar</p>
          <p className="text-2xl font-bold text-[#1e3a8a]" data-testid="objective-value">{obj.value}</p>
          <p className="text-[10px] text-slate-500">{period.label}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Realizat</p>
          <p className="text-2xl font-bold text-slate-800" data-testid="achieved-value">{data.production.total}</p>
          <p className="text-[10px] text-slate-500">{obj.achieved_pct}% din țintă</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Daily static</p>
          <p className="text-xl font-bold text-slate-700" data-testid="daily-static">{daily.static}</p>
          <p className="text-[10px] text-slate-500">{obj.value} / {period.days_in_period} zile</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Daily recalculat</p>
          <p className={`text-xl font-bold ${daily.dynamic > (daily.static * 1.5) ? "text-rose-700" : "text-emerald-700"}`} data-testid="daily-dynamic">
            {daily.dynamic}
          </p>
          <p className="text-[10px] text-slate-500">
            {daily.remaining_to_target} rămase / {period.days_remaining} zile
          </p>
        </div>
      </div>
      {/* Progress bar */}
      <div className="mt-3">
        <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
          <span>Progres către obiectiv</span>
          <span className={obj.on_track ? "text-emerald-700 font-medium" : "text-rose-700 font-medium"}>
            {obj.on_track ? "✓ On track" : "⚠ În urmă"}
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden">
          <div
            className={`h-2 rounded-full transition-all ${
              progressPct >= 100 ? "bg-emerald-500" :
              obj.on_track ? "bg-[#1e3a8a]" : "bg-rose-500"
            }`}
            style={{ width: `${Math.max(progressPct, 2)}%` }}
            data-testid="objective-progress-bar"
          />
        </div>
      </div>
    </div>
  );
}

// ---------- Iter62g — Obiective companie pe produs (din secțiunea Obiective) ----------

function CompanyObjectivesBlock({ cp, periodLabel }) {
  const rows = (cp.categories || []).map((cat) => {
    const obj = cp.objective?.[cat.key] || 0;
    const real = cp.realized_csd?.[cat.key] || 0;
    const upl = cp.uploaded?.[cat.key] || 0;
    const canc = cp.cancelled_csd?.[cat.key] || 0;
    const pct = obj > 0 ? Math.round((real / obj) * 100) : null;
    return { ...cat, obj, real, upl, canc, pct };
  });
  const barColor = (pct) =>
    pct == null ? "bg-slate-300"
    : pct >= 100 ? "bg-emerald-500"
    : pct >= 75 ? "bg-lime-500"
    : pct >= 50 ? "bg-amber-500"
    : "bg-rose-500";
  const fmt = (n) => (n || 0).toLocaleString("ro-RO");

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="company-objectives-block">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-[#1e3a8a]" />
          <span className="text-sm font-semibold text-slate-800">
            Obiective companie pe produs {periodLabel ? `· ${periodLabel}` : ""}
          </span>
        </div>
        <span className="text-[11px] text-slate-500">
          Atingere măsurată după <span className="font-medium text-emerald-700">data CSD</span> ·
          «Încărcat» = după data vânzării
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b">
              <th className="py-1.5 pr-3 text-left font-semibold">Produs</th>
              <th className="py-1.5 px-3 text-right font-semibold">Obiectiv</th>
              <th className="py-1.5 px-3 text-right font-semibold">Realizat (CSD)</th>
              <th className="py-1.5 px-3 text-left font-semibold w-[30%]">Progres</th>
              <th className="py-1.5 pl-3 text-right font-semibold">Încărcat (vânzări)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b last:border-0" data-testid={`company-obj-row-${r.key}`}>
                <td className="py-2 pr-3 font-medium text-slate-700 whitespace-nowrap">
                  {r.label}
                  {r.unit === "puncte" && <span className="ml-1 text-[10px] text-slate-400">puncte</span>}
                </td>
                <td className="py-2 px-3 text-right text-slate-600">{fmt(r.obj)}</td>
                <td className="py-2 px-3 text-right font-semibold text-slate-900">
                  {fmt(r.real)}
                  {r.pct != null && (
                    <span className={`ml-1.5 text-[11px] font-semibold ${r.pct >= 100 ? "text-emerald-600" : r.pct >= 50 ? "text-amber-600" : "text-rose-600"}`}>
                      {r.pct}%
                    </span>
                  )}
                  {r.canc > 0 && (
                    <span className="block text-[10px] font-normal text-rose-500" title="Anulate după CSD — incluse în realizat conform regulii PPC, dar necomisionate">
                      din care {fmt(r.canc)} anulate
                    </span>
                  )}
                </td>
                <td className="py-2 px-3">
                  <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${barColor(r.pct)} transition-[width] duration-500`}
                      style={{ width: `${r.pct == null ? 0 : Math.min(Math.max(r.pct, 2), 100)}%` }}
                    />
                  </div>
                </td>
                <td className="py-2 pl-3 text-right text-slate-500">{fmt(r.upl)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(cp.hvas_unmapped_csd > 0 || cp.hvas_unmapped_uploaded > 0) && (
        <p className="mt-2 text-[11px] text-amber-600" data-testid="company-obj-hvas-unmapped">
          ⚠ HVAS fără punctaj mapat: {cp.hvas_unmapped_csd || 0} contracte (CSD) · {cp.hvas_unmapped_uploaded || 0} (încărcate) — nu sunt incluse în puncte.
        </p>
      )}
      <div className="mt-2 flex justify-end">
        <Link to="/objectives" className="inline-flex items-center gap-1 text-xs text-[#1e3a8a] hover:underline" data-testid="company-obj-open-objectives">
          <Settings className="h-3 w-3" /> Gestionează obiectivele
        </Link>
      </div>
    </div>
  );
}
